import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin, Clock, Star, ChevronDown, Users, Heart, Trophy, Target, Baby, Activity, Zap, CreditCard, Calendar, CheckCircle, Shield, Music, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import "@/App.css";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: "Accueil", path: "/" },
    { name: "Pilates Reformer", path: "/pilates-reformer" },
    { name: "Maman & Bébé", path: "/maman-bebe" },
    { name: "Kangoo Jumps", path: "/kangoo-jumps" },
    { name: "Tarifs & Réservation", path: "/tarifs" },
    { name: "Programmes en ligne", path: "/programmes-en-ligne" },
    { name: "Cartes Cadeaux", path: "/cartes-cadeaux" },
    { name: "Contact", path: "/contact" }
  ];

  return (
    <header className="bg-white shadow-md relative z-50">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_core-strength-6/artifacts/lo9aq05r_7.png" 
              alt="FAMILYNA Pilates Reformer & More" 
              className="h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-sm font-medium transition-colors hover:text-terracotta ${
                  location.pathname === item.path ? 'text-terracotta' : 'text-gray-700'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Contact Info */}
          <div className="hidden md:flex items-center space-x-4 text-sm text-gray-600">
            <Phone className="w-4 h-4" />
            <span>06 98 51 06 83</span>
            <Mail className="w-4 h-4" />
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-lg border-t">
            <nav className="flex flex-col">
              {navItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:text-terracotta hover:bg-orange-50 border-b"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="px-4 py-3 text-sm text-gray-600 border-b">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>06 98 51 06 83</span>
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section Component
const HeroSection = () => {
  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-no-repeat"
        style={{
          backgroundImage: 'linear-gradient(rgba(20,14,8,0.45), rgba(20,14,8,0.55)), url("https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/pag5hsac_photo%20image%20acceuil%20du%20site.png")',
          backgroundPosition: 'center 38%'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
        {/* Studio Badge */}
        <div className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-8">
          <span className="text-sm font-medium tracking-wide">STUDIO 85M²</span>
        </div>

        <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
          Mon Studio 100%<br />
          <span className="text-terracotta">Féminin</span>
        </h1>

        <h2 className="text-2xl md:text-3xl mb-8 max-w-2xl mx-auto leading-relaxed font-light">
          Pilates Reformer & More
        </h2>

        <p className="text-base md:text-lg mb-12 max-w-3xl mx-auto text-gray-200">
          Je vous accueille dans mon studio de 85 m² dédié aux femmes, un espace lumineux et 
          chaleureux pour vous accompagner dans votre pratique du Pilates Reformer
        </p>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a 
            href="https://momoyoga.com/studio-familyna" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="bg-terracotta hover:bg-terracotta-dark text-white px-8 py-3 rounded-full text-lg font-medium"
              data-testid="reserve-cours-btn"
            >
              Réserver un cours →
            </Button>
          </a>
          <Link to="/pilates-reformer">
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-gray-900 px-8 py-3 rounded-full text-lg font-medium"
              data-testid="decouvrir-pilates-btn"
            >
              Découvrir le Pilates
            </Button>
          </Link>
        </div>

        {/* Lien Cartes Cadeaux */}
        <div className="mt-8 text-center">
          <Link to="/cartes-cadeaux">
            <Button
              className="bg-gradient-to-r from-terracotta to-orange-600 hover:from-orange-600 hover:to-terracotta text-white px-8 py-3 rounded-full text-lg font-medium shadow-lg"
              data-testid="cartes-cadeaux-btn"
            >
              <Gift className="w-5 h-5 mr-2" />
              Cartes Cadeaux 🎁
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

// Services Section Component - AVEC VOS PHOTOS AUTHENTIQUES
const ServicesSection = () => {
  const services = [
    {
      title: "Pilates Reformer",
      description: "Cours individuels et collectifs sur machines Reformer professionnelles",
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/i83ji90m_Photo%20image%20pilates%20reformer.png",
      imagePosition: "center 32%",
      duration: "1h",
      intensity: "Modérée à intense",
      color: "bg-orange-100 text-orange-700",
      link: "/pilates-reformer"
    },
    {
      title: "Maman & Bébé",
      description: "Séances spécialement adaptées pour les futures et jeunes mamans",
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/j88j5vqo_Photo%20image%20cours%20maman%20et%20be%CC%81be%CC%81.png",
      imagePosition: "center 20%",
      duration: "45 min",
      intensity: "Douce",
      color: "bg-blue-100 text-blue-700",
      link: "/maman-bebe"
    },
    {
      title: "Kangoo Jumps",
      description: "Cours cardio fun avec des chaussures à ressorts",
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/nvhinp45_Photo%20image%20kangoo.webp",
      imagePosition: "center 28%",
      duration: "1h",
      intensity: "Très dynamique",
      color: "bg-pink-100 text-pink-700",
      link: "/kangoo-jumps"
    },
    {
      title: "Coaching Individuel Femme",
      description: "Accompagnement personnalisé adapté à vos objectifs",
      image: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/0sudg49e_photo%20coaching%20inividuel.jpg",
      imagePosition: "center 30%",
      duration: "1h",
      intensity: "Sur mesure",
      color: "bg-green-100 text-green-700",
      link: "/contact"
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Les différents cours du studio
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Je vous propose un éventail complet d'activités pour répondre à tous vos besoins et envies
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <Card key={index} className="overflow-hidden group hover:shadow-xl transition-all duration-300 bg-white">
              <div className="aspect-square relative overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  style={{ objectPosition: service.imagePosition || 'center' }}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3">
                  <Badge className="bg-white/90 text-gray-700 border-0">
                    {service.duration}
                  </Badge>
                </div>
                <div className="absolute bottom-3 left-3">
                  <Badge className={`${service.color} border-0 text-xs`}>
                    {service.intensity}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <CardTitle className="text-lg font-bold text-gray-900 mb-2">
                  {service.title}
                </CardTitle>
                <CardDescription className="text-sm text-gray-600 mb-4">
                  {service.description}
                </CardDescription>
                <Link to={service.link}>
                  <Button 
                    size="sm" 
                    className="w-full bg-terracotta hover:bg-terracotta-dark text-white"
                  >
                    En savoir plus
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Julia Instructor Section Component
const InstructorSection = () => {
  return (
    <section className="py-20 bg-gradient-to-r from-orange-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Qui suis-je ?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
              Julia, votre instructrice passionnée qui transforme chaque séance en moment de bonheur et de dépassement
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              {/* Photo de Julia */}
              <div className="lg:col-span-1">
                <div className="relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_reformer-rebuild/artifacts/q4sw3ifg_IMG_8129.jpeg"
                    alt="Julia - Instructrice FAMILYNA"
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                  />
                  <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-md">
                    <p className="text-terracotta font-semibold">Julia</p>
                    <p className="text-sm text-gray-600">Fondatrice FAMILYNA</p>
                  </div>
                </div>
              </div>
              
              {/* Ma philosophie */}
              <div className="lg:col-span-1 text-left">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Ma philosophie
                </h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-start space-x-3">
                    <Heart className="w-5 h-5 text-terracotta mt-1 flex-shrink-0" />
                    <p><strong>Bienveillante :</strong> Je suis à l'écoute de chacune et m'adapte à tous les niveaux</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Target className="w-5 h-5 text-terracotta mt-1 flex-shrink-0" />
                    <p><strong>Professionnelle :</strong> J'apporte mon expertise technique et ma pédagogie adaptée</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Trophy className="w-5 h-5 text-terracotta mt-1 flex-shrink-0" />
                    <p><strong>Motivante :</strong> Mon énergie positive est contagieuse et vous porte vers vos objectifs</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Users className="w-5 h-5 text-terracotta mt-1 flex-shrink-0" />
                    <p><strong>Passionnée :</strong> Je transmets mon amour du mouvement avec enthousiasme</p>
                  </div>
                </div>
              </div>
              
              {/* Ce que disent mes élèves */}
              <div className="lg:col-span-1">
                <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl p-6 shadow-lg">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 bg-orange-200 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Heart className="w-8 h-8 text-terracotta" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-900">Ce que disent mes élèves</h4>
                  </div>
                  <div className="space-y-3 text-sm text-gray-600">
                    <p className="italic">"Pleine de good vibes, toujours souriante"</p>
                    <p className="italic">"Son enthousiasme est contagieux"</p>
                    <p className="italic">"Une prof au top, très à l'écoute"</p>
                    <p className="italic">"Elle partage son amour du sport avec énergie"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Google "G" logo icon (official 4-color mark) - used to brand real Google reviews
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12 c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24 c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
    <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039 l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
    <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36 c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
    <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
  </svg>
);

// Google Reviews Section Component - Vrais avis clients FAMILYNA, présentés en carrousel
const GoogleReviewsSection = () => {
  const reviews = [
    {
      name: "Méline Rossillon",
      text: "Julia et son studio sont juste exceptionnels. On se sent dans la bienveillance et la joie de vivre. L'ambiance donne vraiment envie de se donner à fond ! Le Pilate reformer au studio familyna est une révélation pour moi, j'attends mon cours, avec impatience, chaque semaine, depuis déjà plusieurs mois. Ça fait du bien, à l'esprit et au corps. Un studio où règne la sororité, c'est tellement agréable ! En plus, le cours est beaucoup moins cher que partout ailleurs autour de Montpellier ! Je recommande mille fois à quiconque souhaite faire du sport en se faisant plaisir",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 2 semaines"
    },
    {
      name: "Marine Talmon",
      text: "Un endroit coconing est chaleureux. Julia une professeur douce qui nous explique les geste j'adore le pilate réformer je sens mon corps travailler en profondeur après une séance on se sens bien détendu je recommande fortement ce studio 💜",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 3 semaines"
    },
    {
      name: "SoH",
      text: "J'ai commencé le Pilates reformer en novembre et depuis je ne peux plus m'en passer ! Mon corps et mon esprit me remercie 🙏 Julia vous accueille dans un lieu chaleureux et convivial ☀️ Le studio est cocooning et tout est fait pour s'y sentir bien ! Merci Julia pour ta bienveillance, ta douceur et ton professionnalisme. Je ne peux que conseiller le Studio Familyna ✨",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 3 semaines"
    },
    {
      name: "Audrey Glandieres",
      text: "Cela fait maintenant 2 ans que je fais du Kangoojump avec Julia, et j'ai eu la chance également d'essayer des séances de pilates reformer. Ce fut un vrai bonheur car Julia est une prof pleine de good vibes, souriante, dynamique et bienveillante qui sait s'adapter aux besoins de ces éléves. Elle est à l'écoute comme personne, et pour moi cela fait toute la différence.",
      rating: 5,
      course: "Kangoo Jump & Pilates Reformer",
      date: "Il y a 3 semaines"
    },
    {
      name: "Aïda Soumaoro",
      text: "Un cours de Pilates au top ! Cela fait plusieurs mois que je suis les cours de Julia en Pilates reformer. Le studio est à taille humaine, ce qui permet d'avoir une attention particulière de la prof, la salle est propre, très joliment décorée et les équipements sont de qualité. Julia est une prof très pédagogue, douce gentille, attentive mais qui nous pousse et nous challenge avec une force tranquille à laquelle on ne peut résister ! Autre point positif, elle propose des variantes à ses élèves pour s'adapter à leur niveau et aux bobos du jour. Grâce à elle j'ai progressé et je me sens plus confiante dans ma force depuis que je suis ses cours. Je recommande vraiment ce studio et l'accompagnement de Julia.",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a un mois"
    },
    {
      name: "Manon Souques",
      text: "Premier cours aujourd'hui pour moi, et quelle belle expérience ! J'ai passé un moment à la fois sportif et très agréable. On travaille sérieusement dans une ambiance bienveillante, avec des conseils donnés avec professionnalisme et gentillesse. J'ai adoré le lieu ainsi que l'encadrement. Un grand merci pour ce moment ! J'ai déjà réservé un autre cours. Excellente découverte.",
      rating: 5,
      course: "Studio Familyna",
      date: "Il y a 3 mois"
    },
    {
      name: "Yousra Meziane",
      text: "Un endroit juste parfait pour les femmes, on s'y sent vraiment bien ! Julia est une personne adorable, très douce, bienveillante et à l'écoute. On sent qu'elle aime profondément ce qu'elle fait. J'ai adoré mon premier cours de Pilates et j'y retournerai sans hésiter. Je recommande à 100 % : c'est un véritable cocon où l'on se sent bien dès les premières minutes. Merci à toi, Julia",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 4 mois"
    },
    {
      name: "Clea Etard",
      text: "Cela fait longtemps que le reformer me faisait de l'oeil. Le studio est un parfait petit cocon! Merci Julia pour ta gentillesse et ton accompagnement 🥰",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 4 mois"
    },
    {
      name: "ALBACETE Elodie",
      text: "Cela fait maintenant 3 ans que je suis les cours avec Julia, et je la recommande +++ ✨ C'est une prof exceptionnelle : toujours à l'écoute, douce, bienveillante et très professionnelle. Elle sait parfaitement s'adapter à chacune, nous accompagner avec justesse et nous faire progresser en confiance. Son nouveau centre de Pilates Reformer est à son image, chaleureux, apaisant et de grande qualité. On s'y sent tout de suite à l'aise et les séances sont à la fois efficaces et agréables. Une vraie passionnée qui transmet son énergie avec sincérité. Vous pouvez y aller les yeux fermés !",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 4 mois"
    },
    {
      name: "A. Fl",
      text: "Je fréquente le studio pour du réformer depuis janvier. Je suis devenu addict ! Julia est très douce, pleine d'empathie et très à l'écoute. Je vous recommande vraiment de tester ! L'essayer c'est l'adopter 😘",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 4 mois"
    },
    {
      name: "audrey etienne",
      text: "Le studio de Julia est vraiment un endroit hors du temps. Quand on passe la porte, on reçoit toutes ses ondes et énergies positives. On sait qu'on va prendre du temps pour soi, pour son corps et son esprit aussi. Je suis tellement contente d'y aller chaque semaine. Les cours de pilâtes réformer sont dynamiques, motivants, jamais répétitifs. Elle nous fait progresser de séance en séance et sait s'adapter à chacune d'entre nous. Je recommande les yeux fermés!",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 4 mois"
    },
    {
      name: "Celyiah M",
      text: "Superbe studio, Julia est une super prof j'ai toujours hâte d'y retourner les cours sont complets et trop intéressants à chaque fois. Depuis que j'y vais je vois une superbe évolution (je suis en post partum) je recommande cet endroit à toutes les femmes",
      rating: 5,
      course: "Studio Familyna",
      date: "Il y a 4 mois"
    },
    {
      name: "H BELIN",
      text: "Prof géniale, une salle parfaite et un parking super facile. Un vrai moment de plaisir et de bien-être !",
      rating: 5,
      course: "Studio Familyna",
      date: "Il y a 4 mois"
    },
    {
      name: "Madérothérapie drainage",
      text: "Un studio dynamique et accueillant, où l'on se sent vite à l'aise. Les cours de Pilates sont très bien encadrés, parfaits pour travailler le corps en profondeur, et le Kangoo Jump est un vrai boost d'énergie ! Une belle découverte que je recommande à 100 %.",
      rating: 5,
      course: "Pilates Reformer & Kangoo Jump",
      date: "Il y a 8 mois"
    },
    {
      name: "Léa Foncarnier",
      text: "Julia est géniale, à l'écoute, qui prend le temps de corriger les postures et toujours avec le sourire! Foncez pour ses cours de pilâtes réformer ça vaut vraiment le coup!",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 8 mois"
    },
    {
      name: "l laure",
      text: "Une prof extra. Douceur, belle énergie et d'une extrême bienveillance. On peut évoluer à son rythme sans pression et dans la bonne humeur. Des séances qui permettent de faire une petite pause et de se recentrer sur soi-même.",
      rating: 5,
      course: "Studio Familyna",
      date: "Il y a 8 mois"
    },
    {
      name: "Thaïs Lautrou",
      text: "J'ai commencé des cours de Pilates Reformer avec Julia et ils sont top : les cours sont en petit groupe donc on est super bien encadrées, l'ambiance est très agréable et les exercices sont super complets pour travailler tout le corps Merci Julia 😊",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 8 mois"
    },
    {
      name: "Aurore LGN",
      text: "Julia est une excellente prof de Pilates Reformer : pédagogue, douce et attentive. Ses cours se déroulent dans une atmosphère bienveillante et motivante. Je suis ravie de pratiquer avec elle !",
      rating: 5,
      course: "Pilates Reformer",
      date: "Il y a 4 mois"
    },
    {
      name: "Marie Claire",
      text: "Julia m'a fait découvrir le kangoo jump, activité cardio et dynamique. Le cours de Julia est hyper sympa, dynamique, avec beaucoup de positivité et de professionnalisme. Je recommande le cours de Julia !!!!",
      rating: 5,
      course: "Kangoo Jump"
    },
    {
      name: "Elodie",
      text: "Cela fait maintenant 2 ans que je fais du Kangoojump avec Julia, et c'est toujours un vrai moment de bonheur à chaque séance ! Julia est une prof pleine de good vibes, toujours souriante, dynamique et bienveillante. On en ressort boosté, le corps et l'esprit rechargés.",
      rating: 5,
      course: "Kangoo Jump"
    },
    {
      name: "Stéphanie",
      text: "J'ai découvert le KangooJumps grâce à Julia, et depuis je suis complètement accro😅 Julia est pleine d'énergie, hyper motivante et sait parfaitement s'adapter à tous les niveaux. Les cours sont intenses et fun en même temps !",
      rating: 5,
      course: "Kangoo Jump"
    },
    {
      name: "Lisa",
      text: "Très belle découverte d'une discipline insolite cardio/ludique. Julia est pleine d'énergie, tout sourire, et très professionnelle. Elle nous accompagne à notre rythme dans l'apprentissage des chorégraphies, et sans pression. Foncez !",
      rating: 5,
      course: "Kangoo Jump"
    },
    {
      name: "Marion",
      text: "Un sport fun cardio découvert grâce à Julia, une professionnelle bienveillante et positive. A tester absolument !!!... mais attention on devient vite addicte :)",
      rating: 5,
      course: "Kangoo Jump"
    },
    {
      name: "Sarah",
      text: "Je te fais un petit retour de ton coaching au bout d'un mois d'accompagnement. Je suis vraiment ravie !! Je perds des kilos et surtout des cm, mais au delà de ça, je retrouve peu à peu une forme physique, et tu as su cibler exactement ce dont j'avais besoin, en apportant une attention toute particulière au périnée et à la sangle abdominale. Et bien sûr, cerise sur le gâteau, je peux faire mon sport avec mon bébé du coup j'ai une reprise en douceur et je peux penser à moi et mon bien être tout en ayant mon bout de choux près de moi !",
      rating: 5,
      course: "Coaching Individuel"
    },
    {
      name: "Marine",
      text: "J'ai intégré le programme Fit Poussette il y a 1 mois. Je le recommande vraiment pour plusieurs raisons : Ça permet d'être en interaction avec d'autres mamans, d'évoluer ensemble et d'échanger. Ça permet de reprendre le sport en douceur, de prendre soin de son périnée, de sa sangle abdominale, de ses sensations par des exercices bien ciblés et pas trop violents pour le corps et une bonne reprise sportive. Ensuite on est accompagnée par Julia qui est vraiment top, et ça c'est cool pour évoluer.",
      rating: 5,
      course: "Fit Poussette"
    },
    {
      name: "Camille",
      text: "Difficile de savoir comment reprendre une activité sportive en tant que jeune maman. Heureusement, j'ai découvert Fit Poussette : le concept idéal pour retrouver la forme tout en douceur, avec nos bébés ! Julia est top : elle s'adapte à chacune d'entre nous et nous motive tout au long de la séance. Que ce soit pour se remettre en forme, se changer les idées ou rencontrer de nouvelles personnes, faire du sport rend tout tellement plus agréable ! Super programme pour un moment fun assuré, avec un supplément de courbatures en prime !",
      rating: 5,
      course: "Fit Poussette"
    },
    {
      name: "Léa",
      text: "Merci Julia pour ces doux et jolis moments ! Prendre du temps pour soi, sans la charge mentale de se demander comment faire pour l'organisation des enfants, j'adore le concept ! On se régale, et les enfants aussi. Merci pour ta bienveillance (et pour les courbatures) !",
      rating: 5,
      course: "Maman & Bébé"
    },
    {
      name: "Tessa",
      text: "L'occasion de se remettre au sport après l'arrivée de bébé. Julia est très attentive aux besoins de chacune et n'hésite pas à s'occuper des bébés un peu moins coopératifs. Un bon moyen de se faire du bien au corps et à la tête, on adore venir tous les mardis !",
      rating: 5,
      course: "Fit Poussette"
    },
    {
      name: "Roxane",
      text: "Julia est vraiment une super professeur qui nous partage son amour pour son sport avec énergie, sourires, positivité, joie de vivre... elle m'a vraiment fait découvrir et adorer ce sport, qui est à la fois cardio et hyper fun.",
      rating: 5,
      course: "Kangoo Jump"
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-orange-50 via-white to-white" data-testid="google-reviews-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-5 py-2 shadow-md">
            <GoogleIcon className="w-5 h-5" />
            <span className="text-sm font-semibold text-gray-700">Avis Google</span>
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, idx) => (
                <Star key={idx} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
              ))}
            </div>
          </div>
        </div>

        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Elles parlent de <span className="text-terracotta">Familyna</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Découvrez les avis des femmes qui font vivre le studio au quotidien.
          </p>
        </div>

        <Carousel
          opts={{ align: "start", loop: true }}
          className="w-full max-w-6xl mx-auto"
          data-testid="google-reviews-carousel"
        >
          <CarouselContent>
            {reviews.map((review, index) => (
              <CarouselItem
                key={index}
                className="basis-full md:basis-1/2 lg:basis-1/3"
                data-testid={`google-review-slide-${index}`}
              >
                <Card className="h-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 border border-orange-100">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-1">
                        {[...Array(review.rating)].map((_, idx) => (
                          <Star key={idx} className="w-4 h-4 fill-amber-500 text-amber-500" />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        {review.date && (
                          <span className="text-[11px] text-gray-400">{review.date}</span>
                        )}
                        <GoogleIcon className="w-5 h-5 opacity-90" />
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed mb-6 flex-1">"{review.text}"</p>
                    <div className="border-t border-orange-100 pt-4 flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
                        <p className="text-xs text-terracotta font-medium">{review.course}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wide text-gray-400 font-medium">Avis vérifié</span>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex -left-4 lg:-left-12" data-testid="google-reviews-prev-btn" />
          <CarouselNext className="hidden md:flex -right-4 lg:-right-12" data-testid="google-reviews-next-btn" />
        </Carousel>

        <div className="text-center mt-14">
          <a
            href="https://www.google.com/search?q=studio+familyna"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="voir-tous-avis-google-btn"
          >
            <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full px-6">
              <GoogleIcon className="w-4 h-4 mr-2" />
              Voir tous les avis Google
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section Component
const ContactSection = () => {
  return (
    <section className="py-20 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">Contactez-moi</h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Prête à commencer votre parcours ? Je suis là pour vous accompagner dans cette belle aventure
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Card className="bg-gray-800 border-amber-400/20 hover:border-amber-400/50 transition-colors duration-300">
            <CardContent className="p-6 text-center">
              <Phone className="w-8 h-8 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Téléphone</h3>
              <p className="text-gray-300">06 98 51 06 83</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-amber-400/20 hover:border-amber-400/50 transition-colors duration-300">
            <CardContent className="p-6 text-center">
              <Mail className="w-8 h-8 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Email</h3>
              <p className="text-gray-300">juliamamancoach@gmail.com</p>
            </CardContent>
          </Card>

          <Card className="bg-gray-800 border-amber-400/20 hover:border-amber-400/50 transition-colors duration-300">
            <CardContent className="p-6 text-center">
              <MapPin className="w-8 h-8 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Studio</h3>
              <p className="text-gray-300">11 bis rue de l'enclos<br />34570 Pignan</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Bandeau Abonnements Reformer - Encart promo sur la page d'accueil
const AbonnementsBanner = () => (
  <section className="py-14 bg-white" data-testid="abonnements-banner-section">
    <div className="container mx-auto px-4">
      <div className="max-w-5xl mx-auto bg-gradient-to-r from-pink-100 via-orange-50 to-pink-100 border border-orange-100 rounded-3xl shadow-lg px-8 py-10 md:px-12 md:py-12 flex flex-col md:flex-row items-center justify-between gap-8">
        <div className="text-center md:text-left">
          <span className="inline-block bg-white text-terracotta text-xs font-semibold uppercase tracking-wide px-4 py-1.5 rounded-full mb-4 shadow-sm">
            Nouveau
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Faites du Reformer une vraie routine
          </h3>
          <p className="text-gray-600 max-w-xl">
            Nos abonnements Reformer dès 75€/mois vous offrent un rendez-vous régulier au studio, à un tarif avantageux.
          </p>
        </div>
        <Link to="/pilates-reformer#abonnements-reformer" className="flex-shrink-0">
          <Button
            className="bg-terracotta text-white hover:bg-terracotta-dark rounded-full px-8 py-6 text-base font-semibold whitespace-nowrap"
            data-testid="voir-abonnements-btn"
          >
            Voir les abonnements →
          </Button>
        </Link>
      </div>
    </div>
  </section>
);

// Home Page Component - RÉORGANISÉ : Cours puis Statistiques
const HomePage = () => {
  useEffect(() => {
    const testAPI = async () => {
      try {
        const response = await axios.get(`${API}/`);
        console.log("API Test:", response.data.message);
      } catch (error) {
        console.error("API Error:", error);
      }
    };
    testAPI();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <ServicesSection />
      <AbonnementsBanner />
      <InstructorSection />
      <GoogleReviewsSection />
      <ContactSection />
    </div>
  );
};

// Pilates Reformer Page Component
const PilatesReformerPage = () => {
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      }
    }
  }, []);

  const benefits = [
    {
      icon: <Target className="w-8 h-8 text-terracotta" />,
      title: "Renforcement Musculaire Profond",
      description: "Travail en profondeur de tous les muscles, notamment les muscles stabilisateurs souvent négligés dans d'autres pratiques."
    },
    {
      icon: <Users className="w-8 h-8 text-terracotta" />,
      title: "Amélioration de la Posture", 
      description: "Développement de la conscience corporelle et correction des déséquilibres posturaux pour un maintien naturel et élégant."
    },
    {
      icon: <Heart className="w-8 h-8 text-terracotta" />,
      title: "Fluidité et Grâce",
      description: "Développement de mouvements fluides et harmonieux qui se reflètent dans votre quotidien."
    },
    {
      icon: <Trophy className="w-8 h-8 text-terracotta" />,
      title: "Bien-être Mental",
      description: "Connexion corps-esprit qui apporte calme, concentration et confiance"
    }
  ];

  const features = [
    {
      icon: <Heart className="w-12 h-12 text-terracotta" />,
      title: "Mouvement",
      description: "Développement de la fluidité et de la grâce dans chaque mouvement"
    },
    {
      icon: <Target className="w-12 h-12 text-terracotta" />,
      title: "Contrôle", 
      description: "Maîtrise parfaite de chaque geste pour un travail précis et efficace"
    },
    {
      icon: <Users className="w-12 h-12 text-terracotta" />,
      title: "Petit Groupe",
      description: "Maximum 6 personnes pour un accompagnement personnalisé"
    },
    {
      icon: <Clock className="w-12 h-12 text-terracotta" />,
      title: "1 Heure",
      description: "Durée optimale pour un entraînement complet et efficace"
    }
  ];

  const pricingPlans = [
    {
      title: "Cours Unitaire",
      price: "25€",
      description: "Par cours d'1 heure"
    },
    {
      title: "Carte 5 cours", 
      price: "120€",
      originalPrice: "125€",
      savings: "Économie de 5€"
    },
    {
      title: "Carte 10 cours",
      price: "230€", 
      originalPrice: "250€",
      savings: "Économie de 20€"
    },
    {
      title: "Carte 20 cours",
      price: "400€",
      originalPrice: "500€", 
      savings: "Économie de 100€"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="pilates-reformer-page">
      {/* Hero Section */}
      <section className="pt-24 pb-20 bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold text-terracotta mb-8">
                Pilates Réformer
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Cours d'1 heure en petit groupe avec 6 machines. Travail spécifique sur machine réformer pour travailler l'ensemble du corps en profondeur.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                {["Mouvement", "Fluidité", "Contrôle", "Conscience du mouvement"].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-terracotta rounded-full"></div>
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>

              <a 
                href="https://momoyoga.com/studio-familyna" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-terracotta hover:bg-terracotta-dark text-white px-8 py-3 rounded-lg text-lg"
                  data-testid="reserve-cours-pilates-btn"
                >
                  Réserver un cours →
                </Button>
              </a>
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/y970mgrv_IMG_4314%202.webp"
                alt="Cours Pilates Reformer FAMILYNA avec Julia"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                <div className="text-2xl font-bold text-terracotta">1h</div>
                <div className="text-sm text-gray-600">par cours</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-terracotta text-center mb-16">
            Qu'est-ce que le Pilates Réformer ?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center p-8 border-orange-100 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-0">
                  <div className="bg-orange-50 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-bold text-terracotta mb-4">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-orange-100">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-terracotta mb-16">
            Les Bienfaits du Pilates Réformer
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="bg-white rounded-full p-3 shadow-md flex-shrink-0">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-700 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/wev5ff0t_att.KBNXful9DJ2isvIJJuzHtlnminmo6ne-_3ZFZhAyBrk%203.webp"
                alt="Groupe en cours de Pilates Reformer au Studio FAMILYNA"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section Abonnements Reformer */}
      <section id="abonnements-reformer" className="py-20 bg-gradient-to-br from-orange-50 to-pink-50" data-testid="abonnements-reformer-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <span className="inline-block bg-terracotta/10 text-terracotta text-xs font-semibold uppercase tracking-wide px-4 py-1.5 rounded-full">
              Pratique régulière
            </span>
          </div>
          <h2 className="text-4xl font-bold text-terracotta text-center mb-4">
            Les Abonnements Reformer
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-2xl mx-auto mb-14">
            Envie de faire du Reformer une vraie routine ? Les abonnements Familyna permettent de pratiquer chaque semaine à un tarif plus avantageux, tout en conservant un rendez-vous régulier au studio.
          </p>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Essentiel */}
            <Card className="p-8 border-orange-100 hover:shadow-lg transition-shadow duration-300" data-testid="abonnement-essentiel-card">
              <CardContent className="p-0 text-center">
                <h3 className="text-2xl font-bold text-terracotta mb-2">Essentiel</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  75€<span className="text-lg font-normal text-gray-500">/mois</span>
                </div>
                <p className="text-gray-700 font-medium mt-4 mb-2">1 cours de Pilates Reformer par semaine</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Soit un rendez-vous régulier chaque semaine pour progresser et installer le Reformer dans sa routine, ou 4 séances par mois à utiliser quand vous le souhaitez.
                </p>
                <a href="https://momoyoga.com/studio-familyna" target="_blank" rel="noopener noreferrer">
                  <Button
                    className="mt-6 w-full bg-white border-2 border-terracotta text-terracotta hover:bg-orange-50 rounded-lg py-3"
                    data-testid="choisir-essentiel-btn"
                  >
                    Choisir Essentiel
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Équilibre - formule mise en avant */}
            <Card className="p-8 border-2 border-terracotta shadow-xl relative hover:shadow-2xl transition-shadow duration-300 lg:scale-105" data-testid="abonnement-equilibre-card">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-terracotta text-white text-xs font-semibold uppercase tracking-wide px-4 py-1.5 rounded-full shadow-md whitespace-nowrap">
                Le plus choisi
              </div>
              <CardContent className="p-0 text-center">
                <h3 className="text-2xl font-bold text-terracotta mb-2">Équilibre</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">
                  135€<span className="text-lg font-normal text-gray-500">/mois</span>
                </div>
                <p className="text-gray-700 font-medium mt-4 mb-2">2 cours de Pilates Reformer par semaine ou 8 crédits par mois</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Pour une pratique plus régulière et davantage de progression tout au long de l'année.
                </p>
                <a href="https://momoyoga.com/studio-familyna" target="_blank" rel="noopener noreferrer">
                  <Button
                    className="mt-6 w-full bg-terracotta hover:bg-terracotta-dark text-white rounded-lg py-3"
                    data-testid="choisir-equilibre-btn"
                  >
                    Choisir Équilibre
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-4">
            <span className="inline-block bg-gray-100 text-gray-500 text-xs font-semibold uppercase tracking-wide px-4 py-1.5 rounded-full">
              Pratique libre, sans abonnement
            </span>
          </div>
          <h2 className="text-4xl font-bold text-terracotta text-center mb-16">
            Tarifs Pilates Réformer
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <Card key={index} className="text-center p-8 border-orange-100 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
                <CardContent className="p-0">
                  <h3 className="text-xl font-bold text-terracotta mb-6">{plan.title}</h3>
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-gray-900 mb-2">{plan.price}</div>
                    {plan.originalPrice && (
                      <div className="text-lg text-gray-400 line-through mb-1">{plan.originalPrice}</div>
                    )}
                    {plan.savings && (
                      <div className="text-sm text-green-600 font-semibold">{plan.savings}</div>
                    )}
                    {plan.description && (
                      <div className="text-sm text-gray-600 mt-2">{plan.description}</div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Button 
              className="bg-terracotta hover:bg-terracotta-dark text-white px-8 py-4 rounded-lg text-lg"
              data-testid="choisir-formule-btn"
            >
              Choisir ma formule →
            </Button>
          </div>
        </div>
      </section>

      {/* Galerie du Studio - Mosaïque photo authentique */}
      <section className="py-20 bg-orange-50" data-testid="studio-gallery-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Dans les coulisses du <span className="text-terracotta">studio</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un espace lumineux et chaleureux, pensé pour vous à chaque détail
            </p>
          </div>

          <div className="columns-2 md:columns-3 gap-4 max-w-5xl mx-auto">
            {[
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/pe9jcix9_IMG_4310%202.webp",
                alt: "Coin rangement et espace enfants du studio Familyna"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/s3jgk2fd_IMG_4307%202.webp",
                alt: "Salle de Pilates Reformer du studio Familyna"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/nkg7vae3_IMG_4308%202.webp",
                alt: "Néon Familyna et déco du studio"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/w7ltprvo_IMG_4306%202.webp",
                alt: "Citation Everyday is a good day to do Pilates"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/xbgjverl_IMG_4316%202.webp",
                alt: "Reflet du studio Familyna avec cours de Pilates Reformer"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/ev7nukan_IMG_4322%203.webp",
                alt: "Julia en plein cours de Pilates Reformer"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/hghrj260_IMG_2951.webp",
                alt: "Cours collectif de Pilates Reformer avec ballons"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/t4q2yxdj_att.pYHxUlcbHPBJDbB2bcyYdoDKj4Kds-ctjZpK5UMupr0%203.webp",
                alt: "Julia enseignant un cours de Pilates Reformer"
              },
              {
                src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/vo3l1ems_att.jwd0VIzkh_qUJSuls4Tu3sOLw5TS-isa2QzYf3l2zEM%203.webp",
                alt: "Julia accompagnant une élève en cours de Pilates Reformer"
              }
            ].map((photo, index) => (
              <div
                key={index}
                className="mb-4 overflow-hidden rounded-2xl shadow-lg break-inside-avoid group"
                data-testid={`studio-gallery-photo-${index}`}
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// NOUVELLE PAGE MAMAN & BÉBÉ - avec vos photos authentiques
const MamanBebePage = () => {
  return (
    <div className="min-h-screen" data-testid="maman-bebe-page">
      {/* Hero Section - Univers Maman & Bébé */}
      <section className="pt-24 pb-20 bg-gradient-to-br from-orange-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
              <h1 className="text-5xl font-bold text-terracotta-light mb-8">
                Univers Maman & Bébé
              </h1>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Coaching personnalisé pour femme enceinte et post-partum. Cours collectifs maman-bébé pour diminuer la charge mentale.
              </p>
              
              {/* Services avec icônes cœur */}
              <div className="space-y-4 mb-8">
                {[
                  "Coaching femme enceinte",
                  "Coaching post-partum", 
                  "Cours maman-bébé"
                ].map((service, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Heart className="w-5 h-5 text-terracotta-light fill-current" />
                    <span className="text-gray-700 font-medium">{service}</span>
                  </div>
                ))}
              </div>

              <a 
                href="https://momoyoga.com/studio-familyna" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-terracotta-light hover:bg-terracotta text-white px-8 py-3 rounded-lg text-lg"
                  data-testid="reserve-cours-maman-btn"
                >
                  Réserver un cours →
                </Button>
              </a>
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_core-strength-6/artifacts/z4vdnxab_DM9A6846.jpeg"
                alt="Cours Maman & Bébé FAMILYNA"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                <div className="flex items-center space-x-2">
                  <Baby className="w-5 h-5 text-terracotta-light" />
                  <span className="text-sm font-medium text-gray-700">Avec bébé</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Une Expertise Dédiée aux Femmes - Mamans */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-terracotta-light mb-6">
              Une Expertise Dédiée aux Femmes - Mamans
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Grâce à mon expertise de coach sportif spécialisé en accompagnement pour la femme enceinte et le post-partum, 
              vous bénéficiez d'un suivi personnalisé et adapté à chaque étape de votre parcours.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Femme Enceinte */}
            <Card className="text-center p-8 border-pink-100 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-0">
                <div className="bg-pink-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Baby className="w-10 h-10 text-terracotta-light" />
                </div>
                <h3 className="text-2xl font-bold text-terracotta-light mb-4">Femme Enceinte</h3>
                <p className="text-gray-600 leading-relaxed">
                  Accompagnement personnalisé pour maintenir votre forme physique en toute sécurité 
                  durant votre grossesse et préparer votre accouchement.
                </p>
              </CardContent>
            </Card>

            {/* Post-Partum */}
            <Card className="text-center p-8 border-pink-100 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-0">
                <div className="bg-pink-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-10 h-10 text-terracotta-light" />
                </div>
                <h3 className="text-2xl font-bold text-terracotta-light mb-4">Post-Partum</h3>
                <p className="text-gray-600 leading-relaxed">
                  Récupération en douceur après l'accouchement avec des exercices adaptés 
                  pour retrouver progressivement votre forme physique.
                </p>
              </CardContent>
            </Card>

            {/* Fit Poussette */}
            <Card className="text-center p-8 border-pink-100 hover:shadow-lg transition-shadow duration-300">
              <CardContent className="p-0">
                <div className="bg-pink-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-terracotta-light" />
                </div>
                <h3 className="text-2xl font-bold text-terracotta-light mb-4">Fit Poussette</h3>
                <p className="text-gray-600 leading-relaxed">
                  Partagez un moment privilégié avec votre bébé tout en prenant soin 
                  de votre corps et de votre bien-être.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section Coaching Individualisé */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-orange-100">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_reformer-rebuild/artifacts/j09ai3j1_DM9A7035.jpeg"
                alt="Coaching individualisé femme FAMILYNA"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
              <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-lg">
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-terracotta-light" />
                  <span className="text-sm font-medium text-gray-700">Sur mesure</span>
                </div>
              </div>
            </div>
            
            <div>
              <h2 className="text-4xl font-bold text-terracotta-light mb-8">
                Accompagnement et Coaching Individualisé
              </h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Un suivi personnalisé pour les femmes enceintes et en post-partum. 
                Chaque séance est adaptée à vos besoins spécifiques et à votre progression.
              </p>

              {/* Avantages du coaching individualisé */}
              <div className="space-y-6 mb-8">
                <div className="flex items-start space-x-3">
                  <Heart className="w-5 h-5 text-terracotta-light mt-1 fill-current" />
                  <div>
                    <h3 className="font-bold text-gray-900">Suivi personnalisé</h3>
                    <p className="text-gray-700">Programme adapté à votre condition physique et vos objectifs</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Target className="w-5 h-5 text-terracotta-light mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900">Expertise spécialisée</h3>
                    <p className="text-gray-700">Accompagnement par une professionnelle formée aux spécificités féminines</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Trophy className="w-5 h-5 text-terracotta-light mt-1" />
                  <div>
                    <h3 className="font-bold text-gray-900">Flexibilité totale</h3>
                    <p className="text-gray-700">Horaires et intensité adaptés à votre rythme de vie</p>
                  </div>
                </div>
              </div>

              {/* Tarif */}
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-terracotta-light mb-2">Tarif Coaching Individuel</h3>
                <div className="text-3xl font-bold text-terracotta-light">45€</div>
                <p className="text-sm text-gray-600">par séance d'1 heure</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Fit Poussette détaillée */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-terracotta-light mb-8">Fit Poussette</h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Le cours parfait pour les mamans ! Venez faire du sport avec votre bébé dans nos cours 
                Fit Poussette et diminuez votre charge mentale en n'ayant plus besoin de faire garder votre enfant.
              </p>

              {/* Tarifs Fit Poussette */}
              <div className="bg-white rounded-xl p-6 shadow-lg mb-8">
                <h3 className="text-2xl font-bold text-terracotta-light mb-4">Tarifs Fit Poussette</h3>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xl font-semibold">/cours</div>
                    <div className="text-3xl font-bold text-terracotta-light">12€</div>
                  </div>
                  <div className="text-right">
                    <div className="bg-amber-100 text-terracotta px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                      🎉 1er cours gratuit
                    </div>
                  </div>
                </div>
                <div className="border-t border-orange-100 pt-4">
                  <div className="text-xl font-semibold">Carte 10 cours</div>
                  <div className="text-3xl font-bold text-terracotta-light">120€</div>
                </div>
              </div>

              {/* Avantages */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Avantages :</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <Heart className="w-5 h-5 text-terracotta-light mt-1 fill-current" />
                    <span className="text-gray-700">Moment privilégié avec votre bébé</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Target className="w-5 h-5 text-terracotta-light mt-1" />
                    <span className="text-gray-700">Réduction de la charge mentale</span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Trophy className="w-5 h-5 text-terracotta-light mt-1" />
                    <span className="text-gray-700">Pas besoin de faire garder votre enfant</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_core-strength-6/artifacts/fsnyytqp_DM9A6896.jpeg"
                alt="Cours Fit Poussette"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section Pourquoi Choisir Nos Cours Maman & Bébé */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-terracotta-light mb-12">
                Pourquoi Choisir Nos Cours Maman & Bébé ?
              </h2>
              
              <div className="space-y-8">
                {/* Approche Bienveillante */}
                <div className="flex items-start space-x-4">
                  <div className="bg-pink-50 rounded-full p-3 flex-shrink-0">
                    <Heart className="w-8 h-8 text-terracotta-light fill-current" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Approche Bienveillante</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Un accompagnement en douceur qui respecte votre rythme et vos besoins spécifiques 
                      à chaque étape de la maternité.
                    </p>
                  </div>
                </div>

                {/* Expertise Certifiée */}
                <div className="flex items-start space-x-4">
                  <div className="bg-pink-50 rounded-full p-3 flex-shrink-0">
                    <Shield className="w-8 h-8 text-terracotta-light" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Expertise Certifiée</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Formation spécialisée dans l'accompagnement sportif des femmes enceintes 
                      et en post-partum pour votre sécurité.
                    </p>
                  </div>
                </div>

                {/* Communauté de Mamans */}
                <div className="flex items-start space-x-4">
                  <div className="bg-pink-50 rounded-full p-3 flex-shrink-0">
                    <Users className="w-8 h-8 text-terracotta-light" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Communauté de Mamans</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Rencontrez d'autres mamans dans un environnement bienveillant et créez des liens 
                      durables autour de valeurs communes.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_core-strength-6/artifacts/8ucwbxaj_DM9A6983.jpeg"
                alt="Cours collectifs maman-bébé"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Galerie Authentique - Nos mamans en mouvement */}
      <section className="py-20 bg-gradient-to-r from-orange-50 to-orange-50" data-testid="maman-bebe-gallery-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-terracotta-light mb-4">
              Nos mamans en mouvement
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Des moments authentiques où sport et maternité se conjuguent en douceur
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-xl" data-testid="maman-bebe-gallery-photo-0">
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/94xg9v8x_IMG_9506%202.jpg"
                alt="Maman en plein exercice de Pilates Reformer, bébé posé au sol à ses côtés"
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl" data-testid="maman-bebe-gallery-photo-1">
              <img
                src="https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/qobe8ssb_IMG_9510%202.jpg"
                alt="Deux mamans en mouvement avec leurs bébés dans les bras"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// NOUVELLE PAGE KANGOO JUMPS - avec vos photos authentiques
const KangooJumpsPage = () => (
  <div className="min-h-screen" data-testid="kangoo-jumps-page">
    {/* Hero Section avec citation intégrée */}
    <section className="pt-24 pb-20 bg-gradient-to-br from-orange-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-5xl font-bold text-terracotta mb-6">
              Kangoo Jumps
            </h1>
            
            {/* Citation intégrée sous le titre */}
            <div className="mb-8 p-4 bg-white/80 rounded-lg shadow-sm border-l-4 border-terracotta/30">
              <blockquote className="text-lg font-light text-gray-700 italic">
                "Le mouvement est la base de toute joie"
              </blockquote>
              <p className="text-terracotta font-medium text-sm mt-2">- Julia, FAMILYNA</p>
            </div>
            
            <h2 className="text-2xl text-gray-700 mb-8 font-light">
              Sport cardio, ludique et super fun
            </h2>

            <a 
              href="https://momoyoga.com/studio-familyna" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                className="bg-terracotta-light hover:bg-terracotta text-white px-8 py-3 rounded-lg text-lg"
                data-testid="reserve-kangoo-jumps-btn"
              >
                Réserver Kangoo Jumps →
              </Button>
            </a>
          </div>
          
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_core-strength-6/artifacts/oolbct9k_Capture%20d%E2%80%99e%CC%81cran%202025-10-06%20a%CC%80%2008.57.10.png"
              alt="Cours Kangoo Jumps FAMILYNA"
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Section Vidéo Kangoo Jumps en action */}
    <section className="py-20 bg-gradient-to-br from-orange-50 to-pink-50" data-testid="kangoo-video-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-block bg-terracotta/10 text-terracotta text-xs font-semibold uppercase tracking-wide px-4 py-1.5 rounded-full mb-4">
            Ambiance studio
          </span>
          <h2 className="text-4xl font-bold text-terracotta mb-4">
            Le Kangoo Jumps en action
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Une immersion dans l'énergie et la bonne humeur de nos cours
          </p>
        </div>
        <div className="max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-xl">
          <video
            className="w-full h-auto block"
            poster="/videos/kangoo-jumps-poster.jpg"
            autoPlay
            muted
            loop
            playsInline
            controls
            data-testid="kangoo-jumps-video"
          >
            <source src="/videos/kangoo-jumps.webm" type="video/webm" />
            <source src="/videos/kangoo-jumps.mp4" type="video/mp4" />
          </video>
        </div>
      </div>
    </section>

    {/* Section Pourquoi le Kangoo Jumps */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <h2 className="text-4xl font-bold text-terracotta mb-8">
              Pourquoi le Kangoo Jumps ?
            </h2>
            
            <div className="space-y-6">
              {[
                "Cardio intense avec protection des articulations",
                "Brûle jusqu'à 25% de calories en plus qu'un cours traditionnel",
                "Améliore l'équilibre, la coordination et la proprioception",
                "Ambiance fun et énergétique pour déstresser"
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="bg-pink-100 rounded-full p-3 flex-shrink-0">
                    <Zap className="w-6 h-6 text-terracotta-light" />
                  </div>
                  <div>
                    <p className="text-gray-700 leading-relaxed">{benefit}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_core-strength-6/artifacts/8eta7kmo_Capture%20d%E2%80%99e%CC%81cran%202025-10-06%20a%CC%80%2008.58.48.png"
              alt="Kangoo Jumps en action"
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </div>
        </div>
      </div>
    </section>

    {/* Section Les Bienfaits des Kangoo Jumps */}
    <section className="py-20 bg-gradient-to-r from-orange-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_core-strength-6/artifacts/t0je0kms_Capture%20d%E2%80%99e%CC%81cran%202025-10-06%20a%CC%80%2008.58.16.png"
              alt="Les Bienfaits des Kangoo Jumps"
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </div>

          <div>
            <h2 className="text-4xl font-bold text-terracotta mb-8">
              Les Bienfaits des Kangoo Jumps
            </h2>

            <p className="text-lg text-gray-600 mb-12">
              Un sport révolutionnaire qui prend soin de votre corps
            </p>
            
            <div className="space-y-8">
              {/* Cardio Intensif */}
              <div className="flex items-start space-x-4">
                <div className="bg-pink-100 rounded-full p-3 flex-shrink-0">
                  <Zap className="w-8 h-8 text-terracotta-light" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Cardio Intensif</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Entraînement cardiovasculaire complet qui booste votre endurance et améliore votre condition physique générale.
                  </p>
                </div>
              </div>

              {/* Protection Articulaire */}
              <div className="flex items-start space-x-4">
                <div className="bg-amber-100 rounded-full p-3 flex-shrink-0">
                  <Shield className="w-8 h-8 text-terracotta" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Protection Articulaire</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Les chaussures spéciales réduisent l'impact sur les articulations jusqu'à 80%, protégeant genoux, hanches et dos.
                  </p>
                </div>
              </div>

              {/* Bien-être et Plaisir */}
              <div className="flex items-start space-x-4">
                <div className="bg-pink-100 rounded-full p-3 flex-shrink-0">
                  <Heart className="w-8 h-8 text-terracotta-light" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Bien-être et Plaisir</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Libération d'endorphines garantie ! Une façon amusante de faire du sport qui redonne le sourire.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Section Tarifs Kangoo Jumps */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl font-bold text-terracotta text-center mb-16">
          Tarifs Kangoo Jumps
        </h2>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto mb-12">
          <Card className="text-center p-8 border-pink-100 hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
            <CardContent className="p-0">
              <h3 className="text-xl font-bold text-terracotta-light mb-6">Cours Unitaire</h3>
              <div className="mb-6">
                <div className="text-4xl font-bold text-terracotta-light mb-2">20€</div>
                <div className="text-sm text-gray-600 mt-2">Par cours</div>
              </div>
            </CardContent>
          </Card>

          <Card className="text-center p-8 border-2 border-pink-300 shadow-lg hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
            <CardContent className="p-0">
              <h3 className="text-xl font-bold text-terracotta-light mb-6">Carte 10 cours</h3>
              <div className="mb-6">
                <div className="text-4xl font-bold text-terracotta-light mb-2">150€</div>
                <div className="text-lg text-gray-400 line-through mb-1">200€</div>
                <div className="text-sm text-green-600 font-semibold">Économie de 50€</div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        <div className="text-center">
          <a 
            href="https://momoyoga.com/studio-familyna" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button 
              className="bg-terracotta-light hover:bg-terracotta text-white px-8 py-4 rounded-lg text-lg"
            >
              Réserver maintenant →
            </Button>
          </a>
        </div>
      </div>
    </section>
  </div>
);

// NOUVELLE PAGE TARIFS & RÉSERVATION - Reformer en premier + email ajouté
const dayColorClasses = {
  orange: "bg-orange-100 text-orange-700",
  pink: "bg-pink-100 text-pink-700",
  blue: "bg-blue-100 text-blue-700"
};

const weeklyPlanning = [
  {
    day: "Lundi",
    groups: [
      {
        slots: [
          { time: "9h30 – 13h30", activity: "Pilates Reformer", color: "orange" },
          { time: "18h00", activity: "Pilates Reformer", color: "orange" },
          { time: "19h00", activity: "Kangoo Jumps", color: "pink" }
        ]
      }
    ]
  },
  {
    day: "Mardi",
    emptyNote: "Pas de cours collectifs"
  },
  {
    day: "Mercredi",
    morningNote: "Pas de cours le matin",
    groups: [
      {
        label: "Après-midi",
        slots: [
          { time: "16h00", activity: "Fit Poussette · Renfo Maman/Enfant", color: "blue" },
          { time: "17h00", activity: "Pilates Reformer Maman/Enfant", color: "blue" },
          { time: "18h00", activity: "Pilates Reformer", color: "orange" },
          { time: "19h00", activity: "Kangoo Jumps", color: "pink" }
        ]
      }
    ]
  },
  {
    day: "Jeudi",
    groups: [
      { slots: [{ time: "9h30 – 13h30", activity: "Pilates Reformer", color: "orange" }] }
    ],
    footnote: "Reformer en alternance jeudi/vendredi soir selon les semaines : 18h et/ou 19h"
  },
  {
    day: "Vendredi",
    groups: [
      { slots: [{ time: "9h30 – 13h30", activity: "Pilates Reformer", color: "orange" }] }
    ],
    footnote: "Reformer en alternance jeudi/vendredi soir selon les semaines : 18h et/ou 19h"
  },
  {
    day: "Samedi",
    groups: [
      {
        label: "1 samedi sur 2 · Studio Familyna",
        slots: [
          { time: "10h00", activity: "Pilates Reformer", color: "orange" },
          { time: "11h00", activity: "Pilates Reformer", color: "orange" },
          { time: "12h00", activity: "Pilates Reformer", color: "orange" }
        ]
      },
      {
        label: "1 samedi sur 2",
        slots: [{ time: "Journée", activity: "Kangoo Jumps à Sète", color: "pink" }]
      }
    ],
    footnote: "Possibilité ponctuelle de cours de Pilates au sol certains samedis"
  },
  {
    day: "Dimanche",
    groups: [
      {
        label: "1 dimanche sur 2 · Studio Familyna",
        slots: [
          { time: "11h00", activity: "Pilates Reformer", color: "orange" },
          { time: "12h00", activity: "Pilates Reformer", color: "orange" }
        ]
      },
      {
        label: "Tous les dimanches",
        slots: [{ time: "Après-midi", activity: "Kangoo Jumps à Assas", color: "pink" }]
      }
    ]
  }
];

const TarifsPage = () => (
  <div className="min-h-screen pt-24" data-testid="tarifs-page">
    <div className="container mx-auto px-4">
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold text-terracotta mb-8">Tarifs & Réservation</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Découvrez tous mes tarifs et réservez facilement via MomoYoga
        </p>
      </div>

      {/* Bouton principal MomoYoga */}
      <div className="text-center mb-16">
        <a 
          href="https://momoyoga.com/studio-familyna" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block"
        >
          <Button 
            className="bg-terracotta hover:bg-terracotta-dark text-white px-12 py-4 rounded-lg text-xl font-medium shadow-xl"
            data-testid="reserve-momoyoga-btn"
          >
            🔗 Réserver sur MomoYoga
          </Button>
        </a>
      </div>

      {/* Grilles de tarifs par service - REFORMER EN PREMIER */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {/* Pilates Reformer - EN PREMIER */}
        <Card className="text-center p-6 border-orange-100 hover:shadow-xl transition-all duration-300">
          <CardContent className="p-0">
            <h3 className="text-2xl font-bold text-terracotta mb-6">Pilates Reformer</h3>
            <div className="mb-6">
              <div className="text-2xl font-bold text-terracotta mb-1">25€</div>
              <p className="text-sm text-gray-600 mb-2">/cours</p>
              <div className="text-sm text-gray-600">ou cartes :</div>
              <div className="text-lg font-bold text-terracotta mt-1">120€ - 230€ - 400€</div>
              <p className="text-xs text-gray-600">5, 10 ou 20 cours</p>
            </div>
          </CardContent>
        </Card>

        {/* Fit Poussette */}
        <Card className="text-center p-6 border-pink-100 hover:shadow-xl transition-all duration-300">
          <CardContent className="p-0">
            <h3 className="text-2xl font-bold text-terracotta-light mb-6">Fit Poussette</h3>
            <div className="mb-6">
              <div className="text-2xl font-bold text-terracotta-light mb-1">12€</div>
              <p className="text-sm text-gray-600 mb-2">/cours</p>
              <div className="text-sm text-gray-600">ou</div>
              <div className="text-2xl font-bold text-terracotta-light mt-1">120€</div>
              <p className="text-sm text-gray-600">les 10 cours</p>
              <div className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-semibold mt-3 inline-flex items-center">
                🎉 1er cours gratuit
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kangoo Jumps */}
        <Card className="text-center p-6 border-pink-100 hover:shadow-xl transition-all duration-300">
          <CardContent className="p-0">
            <h3 className="text-2xl font-bold text-terracotta-light mb-6">Kangoo Jumps</h3>
            <div className="mb-6">
              <div className="text-2xl font-bold text-terracotta-light mb-1">20€</div>
              <p className="text-sm text-gray-600 mb-2">/cours</p>
              <div className="text-sm text-gray-600">ou</div>
              <div className="text-2xl font-bold text-terracotta-light mt-1">150€</div>
              <p className="text-sm text-gray-600">les 10 cours</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Coaching Personnalisé - Section séparée */}
      <div className="text-center mb-16">
        <Card className="max-w-md mx-auto p-8 border-amber-200 shadow-lg">
          <CardContent className="p-0">
            <h3 className="text-2xl font-bold text-terracotta mb-6">Coaching Personnalisé</h3>
            <div className="text-4xl font-bold text-terracotta mb-2">45€</div>
            <p className="text-sm text-gray-600">/cours par séance</p>
            <p className="text-xs text-gray-500 mt-2">Forfait long terme sur demande</p>
          </CardContent>
        </Card>
      </div>

      {/* Planning des Cours - 2026/2027 */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-terracotta text-center mb-3">Planning des Cours</h2>
        <p className="text-center text-gray-600 mb-12">Saison 2026/2027</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="weekly-planning-grid">
          {weeklyPlanning.map((day, dayIndex) => (
            <Card
              key={dayIndex}
              className="p-6 border-orange-100 hover:shadow-lg transition-shadow duration-300"
              data-testid={`planning-day-${day.day.toLowerCase()}`}
            >
              <CardContent className="p-0">
                <h3 className="text-xl font-bold text-terracotta mb-4">{day.day}</h3>

                {day.emptyNote ? (
                  <p className="text-sm text-gray-500 italic">{day.emptyNote}</p>
                ) : (
                  <>
                    {day.morningNote && (
                      <p className="text-xs text-gray-500 italic mb-3">{day.morningNote}</p>
                    )}
                    <div className="space-y-4">
                      {day.groups.map((group, groupIndex) => (
                        <div key={groupIndex}>
                          {group.label && (
                            <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-2">
                              {group.label}
                            </p>
                          )}
                          <div className="space-y-2">
                            {group.slots.map((slot, slotIndex) => (
                              <div
                                key={slotIndex}
                                className={`${dayColorClasses[slot.color]} px-3 py-2 rounded-lg text-sm`}
                              >
                                <div className="font-semibold">{slot.time}</div>
                                <div>{slot.activity}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {day.footnote && (
                      <p className="text-[11px] text-gray-500 italic mt-4">{day.footnote}</p>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          {/* Légende des couleurs */}
          <div className="grid grid-cols-3 gap-3 text-xs mb-6">
            <div className="flex items-center space-x-2">
              <div className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs font-medium">PR</div>
              <span className="text-gray-700">Pilates Reformer</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="bg-pink-100 text-pink-700 px-2 py-1 rounded text-xs font-medium">KJ</div>
              <span className="text-gray-700">Kangoo Jumps</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">FP</div>
              <span className="text-gray-700">Fit Poussette</span>
            </div>
          </div>

          {/* Mention Coaching Individuel */}
          <div className="text-center border-t border-gray-100 pt-4" data-testid="coaching-individuel-note">
            <p className="text-sm text-gray-600 italic">
              Coaching individuel pré &amp; post-partum : créneaux sur rendez-vous.
            </p>
          </div>
        </div>
      </div>

      {/* Section Comment Réserver */}
      <div className="grid lg:grid-cols-2 gap-8 mb-16">
        {/* Comment Réserver */}
        <Card className="p-8 border-orange-100">
          <CardContent className="p-0">
            <div className="flex items-center space-x-3 mb-6">
              <Calendar className="w-8 h-8 text-terracotta" />
              <h3 className="text-2xl font-bold text-terracotta">Comment Réserver ?</h3>
            </div>
            <ol className="space-y-3 text-gray-700">
              <li className="flex items-start space-x-3">
                <span className="bg-amber-100 text-terracotta rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
                <span>Cliquez sur "Réserver sur MomoYoga"</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="bg-amber-100 text-terracotta rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
                <span>Créez votre compte ou connectez-vous</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="bg-amber-100 text-terracotta rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
                <span>Choisissez votre cours et créneau</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="bg-amber-100 text-terracotta rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
                <span>Procédez au paiement sécurisé</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="bg-amber-100 text-terracotta rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</span>
                <span>Recevez votre confirmation par email</span>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Besoin d'Aide */}
        <Card className="p-8 border-pink-100">
          <CardContent className="p-0">
            <div className="flex items-center space-x-3 mb-6">
              <Phone className="w-8 h-8 text-terracotta-light" />
              <h3 className="text-2xl font-bold text-terracotta-light">Besoin d'Aide ?</h3>
            </div>
            <p className="text-gray-700 mb-6">Contactez-nous pour toute question :</p>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="bg-pink-100 rounded-full p-2">
                  <Phone className="w-5 h-5 text-terracotta-light" />
                </div>
                <span className="font-semibold">06 98 51 06 83</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-amber-100 rounded-full p-2">
                  <Mail className="w-5 h-5 text-terracotta" />
                </div>
                <span className="font-semibold">juliamamancoach@gmail.com</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-pink-100 rounded-full p-2">
                  <Users className="w-5 h-5 text-terracotta-light" />
                </div>
                <span className="font-semibold">@studiofamilynajulia</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="bg-amber-100 rounded-full p-2">
                  <MapPin className="w-5 h-5 text-terracotta" />
                </div>
                <div>
                  <div className="font-semibold">11 bis rue de l'enclos</div>
                  <div className="text-sm text-gray-600">34570 Pignan</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
);

// NOUVELLE PAGE CONTACT - Moderne et sympa avec charte FAMILYNA
const ContactPage = () => (
  <div className="min-h-screen pt-16" data-testid="contact-page">
    {/* Hero Section - Design ultra moderne */}
    <section className="py-20 bg-gradient-to-br from-orange-50 via-white to-orange-100 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-terracotta/5 to-orange-300/5"></div>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 mb-6 shadow-lg">
            <Heart className="w-5 h-5 text-terracotta mr-3" />
            <span className="text-terracotta font-semibold">Contactez-moi</span>
          </div>
          <h1 className="text-6xl font-bold text-gray-900 mb-8">
            Prête à commencer ?
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Je serai ravie de vous accueillir dans mon univers bien-être et vous accompagner dans votre parcours
          </p>
        </div>
      </div>
    </section>

    {/* Contact Cards Section - Design moderne en cartes */}
    <section className="py-16 -mt-10 relative z-20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {/* Carte Contact Direct */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-terracotta to-terracotta-dark rounded-2xl p-6 text-white text-center mb-6">
                <Phone className="w-8 h-8 mx-auto mb-4" />
                <h3 className="text-xl font-bold">Appel Direct</h3>
              </div>
              <div className="text-center">
                <h4 className="font-bold text-lg text-gray-900 mb-2">Téléphone</h4>
                <p className="text-terracotta font-bold text-xl mb-4">06 98 51 06 83</p>
                <Button className="w-full bg-terracotta hover:bg-terracotta-dark text-white rounded-xl py-3 transition-colors">
                  <Phone className="w-4 h-4 mr-2" />
                  Appeler maintenant
                </Button>
              </div>
            </div>

            {/* Carte Email */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl p-6 text-white text-center mb-6">
                <Mail className="w-8 h-8 mx-auto mb-4" />
                <h3 className="text-xl font-bold">Message Écrit</h3>
              </div>
              <div className="text-center">
                <h4 className="font-bold text-lg text-gray-900 mb-2">Email</h4>
                <p className="text-terracotta font-bold text-sm mb-4 break-all">juliamamancoach@gmail.com</p>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-xl py-3 transition-colors">
                  <Mail className="w-4 h-4 mr-2" />
                  Envoyer un email
                </Button>
              </div>
            </div>

            {/* Carte Instagram */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl p-6 text-white text-center mb-6">
                <Users className="w-8 h-8 mx-auto mb-4" />
                <h3 className="text-xl font-bold">Réseaux Sociaux</h3>
              </div>
              <div className="text-center">
                <h4 className="font-bold text-lg text-gray-900 mb-2">Instagram</h4>
                <p className="text-terracotta font-bold text-lg mb-4">@studiofamilynajulia</p>
                <a href="https://www.instagram.com/studiofamilynajulia" target="_blank" rel="noopener noreferrer" data-testid="instagram-follow-link">
                  <Button className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-xl py-3 transition-all">
                    <Users className="w-4 h-4 mr-2" />
                    Suivre sur Instagram
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Informations Studio - Design split moderne */}
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Informations textuelles */}
            <div>
              <div className="inline-flex items-center bg-terracotta/10 rounded-full px-4 py-2 mb-6">
                <MapPin className="w-5 h-5 text-terracotta mr-2" />
                <span className="text-terracotta font-semibold">Mon Studio</span>
              </div>
              
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                Venez découvrir mon espace bien-être
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-terracotta rounded-full p-3 mt-1">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">Adresse</h3>
                    <p className="text-gray-700 text-lg">
                      <strong>11 bis rue de l'enclos</strong><br />
                      34570 Pignan
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-green-500 rounded-full p-3 mt-1">
                    <Activity className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">Parking</h3>
                    <p className="text-gray-700 text-lg">
                      ✅ Parking disponible devant la salle<br />
                      Accès facile et pratique
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-blue-500 rounded-full p-3 mt-1">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-gray-900 mb-2">Horaires</h3>
                    <div className="text-gray-700 text-lg space-y-1">
                      <p><strong>Lundi - Vendredi :</strong> 9h30 - 20h15</p>
                      <p><strong>Week-end :</strong> Sur rendez-vous</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Plan d'accès Google Maps */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="p-6 border-b">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <MapPin className="w-5 h-5 text-terracotta mr-2" />
                    Plan d'accès
                  </h3>
                </div>
                
                {/* Google Maps Embed */}
                <div className="relative h-80">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2893.123456789!2d3.871234567890!3d43.584567890123!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s11+bis+rue+de+l%27enclos%2C+34570+Pignan!5e0!3m2!1sfr!2sfr!4v1234567890123"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Plan d'accès Studio FAMILYNA"
                  ></iframe>
                </div>
                
                <div className="p-4 bg-orange-50">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>📍 11 bis rue de l'enclos, 34570 Pignan</span>
                    <a 
                      href="https://maps.google.com/?q=11+bis+rue+de+l'enclos,+34570+Pignan" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-terracotta hover:text-terracotta-dark font-semibold"
                    >
                      Ouvrir dans Google Maps →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Galerie du Studio - Page Contact */}
    <section className="py-20 bg-white" data-testid="contact-gallery-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Découvrez le <span className="text-terracotta">studio</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Un espace chaleureux et lumineux qui vous attend au cœur de Pignan
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {[
            {
              src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/a0c16wl8_13a55032-2e84-48d4-a56a-b913b2aeded7.JPG",
              alt: "Julia, coach du Studio Familyna"
            },
            {
              src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/qaeq3sp5_IMG_7748%202.webp",
              alt: "Coin cosy et déco du studio Familyna"
            },
            {
              src: "https://customer-assets-jai6qajn.emergentagent.net/job_12a7b379-bce9-4385-b418-cf6b0e6561b0/artifacts/fvbohehy_IMG_8181.webp",
              alt: "Entrée et espace d'accueil du studio Familyna"
            }
          ].map((photo, index) => (
            <div
              key={index}
              className="rounded-2xl overflow-hidden shadow-lg group"
              data-testid={`contact-gallery-photo-${index}`}
            >
              <img
                src={photo.src}
                alt={photo.alt}
                className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Message discret de Laurent */}
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl p-6 shadow-sm border-l-4 border-terracotta/30">
            <blockquote className="text-gray-600 italic text-sm leading-relaxed mb-3">
              "Derrière chaque femme qui réussit, il y a souvent un partenaire qui croit en elle, 
              l'encourage et la soutient dans ses rêves. Je suis cette personne précieuse dans ta vie."
            </blockquote>
            <div className="text-right">
              <span className="text-gray-500 text-sm">- Laurent</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

// Page FIT POUSSETTE
const FitPoussettePage = () => (
  <div className="min-h-screen">
    {/* BLOC 1 - HERO */}
    <section className="relative pt-24 pb-20 bg-gradient-to-br from-orange-50 to-terracotta/10">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-6xl font-bold text-terracotta mb-6">
              FIT POUSSETTE
            </h1>
            <h2 className="text-3xl font-semibold text-gray-800 mb-6">
              Retour à soi en 120 jours
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Un programme de remise en forme post-partum, pensé par une maman, pour les mamans.<br/>
              À faire chez soi, avec ou sans bébé, à son rythme.
            </p>
            <a 
              href="https://buy.stripe.com/aFa00j1jY4p2d0L3ambZe01"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-terracotta text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-terracotta/90 transition-colors shadow-lg"
            >
              👉 Accéder au programme
            </a>
          </div>
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_3503aca5-9ae5-4a26-b5cd-e06e8d8ae67f/artifacts/w98zu7qs_IMG_1852%202.jpg"
              alt="Julia en mouvement avec son enfant"
              className="w-full h-auto rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </div>
    </section>

    {/* BLOC 2 - INTRODUCTION */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-2xl text-gray-700 leading-relaxed mb-6">
            Après une grossesse, on parle beaucoup du bébé.<br/>
            Mais très peu de la femme qui vient de donner la vie.
          </p>
          <p className="text-xl text-gray-600 leading-relaxed mb-6">
            Le corps change.<br/>
            L'énergie n'est plus la même.<br/>
            Le temps manque.<br/>
            Et penser à soi devient souvent secondaire.
          </p>
          <p className="text-2xl font-semibold text-terracotta">
            FIT POUSSETTE est né de cette réalité.
          </p>
        </div>
      </div>
    </section>

    {/* BLOC 3 - STORYTELLING */}
    <section className="py-20 bg-gradient-to-br from-terracotta/5 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-4xl font-bold text-terracotta mb-12 text-center">
            ✨ Mon histoire
          </h3>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="bg-white rounded-2xl p-4 shadow-xl">
                <video 
                  controls 
                  className="w-full h-auto rounded-xl"
                >
                  <source src="https://customer-assets.emergentagent.com/job_3503aca5-9ae5-4a26-b5cd-e06e8d8ae67f/artifacts/hd5fdllt_68ee76f6-2515-419b-a6b8-4319cd6cd4f3.MP4" type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
                <p className="text-sm text-gray-600 mt-4 text-center italic">
                  "Mon parcours FIT POUSSETTE"
                </p>
              </div>
            </div>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>Après mes deux grossesses, je ne reconnaissais plus mon corps. J'avais des kilos en trop, un ventre que je détestais, et une image de moi que je fuyais. Je me sentais mal, tout simplement.</p>
              
              <p className="font-semibold text-terracotta">Et puis un jour, j'ai eu un déclic.</p>
              
              <p>J'ai réalisé que je ne renvoyais pas à mes enfants l'image d'une maman bien dans sa peau, épanouie, confiante. Et ça, c'était hors de question.</p>
              
              <p>Je ne voulais pas leur transmettre les complexes, cette idée qu'on doit se cacher ou s'excuser d'exister. Non. On doit s'aimer. Tel qu'on est.</p>
              
              <p>Mais j'avais peur. Peur de ne jamais retrouver mon corps d'avant. Peur de me trimballer ces kilos toute ma vie.</p>
              
              <p>Et puis j'ai rencontré Maria. Une coach à Miami, maman de trois enfants. Sublime. Rayonnante. On a fait un cours ensemble. Et là je me suis dit : <em>« Si elle y arrive… pourquoi pas moi ? »</em></p>
              
              <p className="font-semibold">Alors j'ai pris une décision. J'ai arrêté d'attendre. Et j'ai commencé à m'organiser, chez moi, avec ce que j'avais.</p>
              
              <p>Ce n'était pas facile. Deux enfants en bas âge. L'école à la maison. L'allaitement. Le travail. Le quotidien.</p>
              
              <p className="font-semibold text-terracotta">Mais j'ai tenu bon.</p>
              
              <p>Parce qu'au fond de moi, je savais : si je ne faisais rien, je risquais de sombrer.</p>
              
              <p>Mon corps a changé. Mais surtout, mon regard sur moi a changé.</p>
              
              <p className="text-lg font-semibold">Aujourd'hui, j'ai transformé cette expérience en un programme complet, pensé pour toi, maman. Pour que tu n'attendes plus. Pour que toi aussi, tu te reconnectes à ton corps, à ton énergie, à ta fierté.</p>
              
              <p className="text-xl font-bold text-terracotta">Parce que si moi j'y suis arrivée, toi aussi tu peux. Et je serai là pour t'accompagner.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* BLOC 4 - À QUI S'ADRESSE */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-4xl font-bold text-terracotta mb-12 text-center">
            À qui s'adresse FIT POUSSETTE ?
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-orange-50 rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-terracotta mt-1 flex-shrink-0" />
                <p className="text-gray-700">Aux mamans en post-partum qui veulent reprendre le mouvement en sécurité</p>
              </div>
            </div>
            <div className="bg-orange-50 rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-terracotta mt-1 flex-shrink-0" />
                <p className="text-gray-700">À celles qui n'ont pas le temps (ni l'envie) d'aller en salle</p>
              </div>
            </div>
            <div className="bg-orange-50 rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-terracotta mt-1 flex-shrink-0" />
                <p className="text-gray-700">À celles qui veulent bouger sans laisser leur bébé</p>
              </div>
            </div>
            <div className="bg-orange-50 rounded-2xl p-8">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-terracotta mt-1 flex-shrink-0" />
                <p className="text-gray-700">À celles qui veulent retrouver de l'énergie et de la confiance</p>
              </div>
            </div>
            <div className="bg-orange-50 rounded-2xl p-8 md:col-span-2">
              <div className="flex items-start space-x-4">
                <CheckCircle className="w-6 h-6 text-terracotta mt-1 flex-shrink-0" />
                <p className="text-gray-700">À celles qui ont besoin d'un cadre, pas de pression</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* BLOC 5 - CE QUE LE PROGRAMME N'EST PAS */}
    <section className="py-20 bg-gradient-to-br from-terracotta/5 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-4xl font-bold text-terracotta mb-12 text-center">
            FIT POUSSETTE, ce n'est pas…
          </h3>
          <div className="space-y-6 mb-8">
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <X className="w-6 h-6 text-red-500 flex-shrink-0" />
              <p>Un programme fitness "avant / après"</p>
            </div>
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <X className="w-6 h-6 text-red-500 flex-shrink-0" />
              <p>Une course à la perte de poids</p>
            </div>
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <X className="w-6 h-6 text-red-500 flex-shrink-0" />
              <p>Une pression de plus dans ton quotidien</p>
            </div>
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <X className="w-6 h-6 text-red-500 flex-shrink-0" />
              <p>Un programme rigide à suivre à la perfection</p>
            </div>
          </div>
          <p className="text-2xl font-semibold text-terracotta text-center">
            👉 C'est un chemin, pas une performance.
          </p>
        </div>
      </div>
    </section>

    {/* BLOC 6 - CONTENU DU PROGRAMME */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h3 className="text-4xl font-bold text-terracotta mb-8 text-center">
            Ce que contient le programme FIT POUSSETTE
          </h3>
          <p className="text-xl text-gray-600 text-center mb-12">
            FIT POUSSETTE est un programme en ligne de 4 mois, accessible via l'application privée Familyna.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-2xl p-8">
              <div className="text-2xl font-bold text-terracotta mb-4">Mois 1 – Reconnexion & reprise</div>
              <p className="text-gray-700">Reprendre le mouvement en douceur, écouter son corps, se reconnecter à ses sensations.</p>
            </div>
            <div className="bg-green-50 rounded-2xl p-8">
              <div className="text-2xl font-bold text-terracotta mb-4">Mois 2 – Renforcement & stabilité</div>
              <p className="text-gray-700">Séances progressives adaptées au post-partum, avec ou sans bébé.</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-8">
              <div className="text-2xl font-bold text-terracotta mb-4">Mois 3 – Continuité & confiance</div>
              <p className="text-gray-700">Installer une routine réaliste, gagner en énergie et en assurance.</p>
            </div>
            <div className="bg-pink-50 rounded-2xl p-8">
              <div className="text-2xl font-bold text-terracotta mb-4">Mois 4 – La suite</div>
              <p className="text-gray-700">Nutrition, mindset, organisation et autonomie pour continuer après le programme.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* BLOC 7 - COMMENT ÇA SE PASSE */}
    <section className="py-20 bg-gradient-to-br from-terracotta/5 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-4xl font-bold text-terracotta mb-12 text-center">
            Comment ça se passe concrètement ?
          </h3>
          <div className="space-y-6">
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <CheckCircle className="w-6 h-6 text-terracotta flex-shrink-0" />
              <p>Accès immédiat après inscription</p>
            </div>
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <CheckCircle className="w-6 h-6 text-terracotta flex-shrink-0" />
              <p>Vidéos disponibles à tout moment</p>
            </div>
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <CheckCircle className="w-6 h-6 text-terracotta flex-shrink-0" />
              <p>Tu avances à ton rythme</p>
            </div>
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <CheckCircle className="w-6 h-6 text-terracotta flex-shrink-0" />
              <p>Chez toi, quand tu peux</p>
            </div>
            <div className="flex items-center space-x-4 text-lg text-gray-700">
              <CheckCircle className="w-6 h-6 text-terracotta flex-shrink-0" />
              <p>Avec ou sans ton bébé</p>
            </div>
          </div>
          <p className="text-2xl font-semibold text-terracotta text-center mt-8">
            👉 Tu n'es pas seule, mais tu es libre.
          </p>
        </div>
      </div>
    </section>

    {/* BLOC 8 - PRIX */}
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-terracotta mb-8">
            Accéder au programme
          </h3>
          <p className="text-xl text-gray-600 mb-6">
            L'accès complet au programme FIT POUSSETTE, incluant les 4 mois de contenu, est proposé au tarif de :
          </p>
          <div className="bg-gradient-to-br from-terracotta/10 to-orange-50 rounded-3xl p-12 mb-8">
            <div className="text-6xl font-bold text-terracotta mb-2">149 €</div>
            <p className="text-gray-600">(paiement unique)</p>
          </div>
          <a 
            href="https://buy.stripe.com/aFa00j1jY4p2d0L3ambZe01"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-terracotta text-white px-10 py-5 rounded-full text-xl font-semibold hover:bg-terracotta/90 transition-colors shadow-xl"
          >
            👉 Accéder au programme
          </a>
        </div>
      </div>
    </section>

    {/* BLOC 9 - MOT DE FIN */}
    <section className="py-20 bg-gradient-to-br from-terracotta/5 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_3503aca5-9ae5-4a26-b5cd-e06e8d8ae67f/artifacts/pxof27rv_IMG_2003%202.JPEG"
                alt="Julia avec ses enfants"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
            <div className="text-center lg:text-left">
              <p className="text-2xl text-gray-700 leading-relaxed mb-6">
                FIT POUSSETTE n'est pas seulement un programme.<br/>
                C'est ce que j'aurais aimé avoir après mes grossesses.
              </p>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                Si tu ressens que c'est le bon moment pour toi,<br/>
                je serai heureuse de t'accueillir dans ce parcours.
              </p>
              <p className="text-3xl font-bold text-terracotta">
                — Julia 💛
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);



// Page Bienvenue FIT POUSSETTE
const BienvenueFitPoussettePage = () => (
  <div className="min-h-screen pt-16">
    {/* BLOC 1 - HERO */}
    <section className="py-20 bg-gradient-to-br from-terracotta/10 via-orange-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-6xl font-bold text-terracotta mb-8">
            💛 Bienvenue dans FIT POUSSETTE
          </h1>
          <h2 className="text-3xl text-gray-700 leading-relaxed mb-6">
            Merci pour ta confiance.
          </h2>
          <p className="text-2xl text-gray-600 leading-relaxed">
            Tu viens de faire un pas important pour toi,<br/>
            et je suis vraiment heureuse de t'accueillir ici.
          </p>
        </div>
      </div>
    </section>

    {/* BLOC 2 - CONFIRMATION & RASSURANCE */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 mb-8">
            <p className="text-xl text-gray-700 leading-relaxed mb-4">
              <CheckCircle className="inline w-8 h-8 text-green-600 mr-2" />
              Ton paiement a bien été pris en compte ✅
            </p>
            <p className="text-xl text-gray-700 leading-relaxed">
              FIT POUSSETTE est désormais en cours d'activation pour toi.
            </p>
          </div>
          <p className="text-xl text-gray-600 italic">
            Tout se fait simplement, pas à pas.
          </p>
        </div>
      </div>
    </section>

    {/* BLOC 3 - PROCHAINES ÉTAPES */}
    <section className="py-16 bg-gradient-to-br from-terracotta/5 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-4xl font-bold text-terracotta mb-12 text-center">
            👉 Prochaine étape : accéder à l'application Familyna
          </h3>
          
          <div className="bg-white rounded-3xl p-10 shadow-xl space-y-8">
            <div className="flex items-start space-x-6">
              <div className="bg-terracotta text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                1
              </div>
              <div>
                <p className="text-xl text-gray-700 leading-relaxed mb-4">
                  <strong>Accède à l'application Familyna via ce lien :</strong>
                </p>
                <a 
                  href="https://familyna.passion.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-terracotta text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-terracotta/90 transition-colors shadow-lg"
                >
                  👉 Ouvrir l'application Familyna
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="bg-terracotta text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                2
              </div>
              <div>
                <p className="text-xl text-gray-700 leading-relaxed">
                  <strong>Crée ton compte</strong> avec l'email utilisé lors du paiement
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-6">
              <div className="bg-terracotta text-white rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                3
              </div>
              <div>
                <p className="text-xl text-gray-700 leading-relaxed mb-4">
                  Une fois ton compte créé,<br/>
                  👉 <strong>je t'ajoute manuellement au programme FIT POUSSETTE</strong>
                </p>
                <div className="bg-blue-50 rounded-xl p-6">
                  <p className="text-lg text-gray-700">
                    ⏱️ Ton accès sera activé sous <strong>24 heures maximum</strong><br/>
                    <span className="text-terracotta">(souvent bien plus rapidement 💛)</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* BLOC 4 - PRÉCISION TECHNIQUE */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-blue-50 border-l-4 border-blue-400 rounded-xl p-8">
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              <span className="text-2xl mr-2">ℹ️</span>
              <strong>Familyna</strong> est une application en ligne sécurisée (Passion.io).<br/>
              Il n'y a <strong>aucun téléchargement</strong> via l'App Store ou Google Play.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Une fois connectée, tu pourras ajouter Familyna sur l'écran d'accueil de ton téléphone,<br/>
              comme une application classique.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* BLOC 5 - MESSAGE HUMAIN & RASSURANT */}
    <section className="py-16 bg-gradient-to-br from-terracotta/5 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-2xl text-gray-700 leading-relaxed mb-6">
            FIT POUSSETTE n'est pas un programme à "réussir".
          </p>
          <p className="text-xl text-gray-600 leading-relaxed mb-6">
            C'est un espace pour reprendre le mouvement, à ton rythme,<br/>
            dans le respect de ton corps et de ton quotidien de maman.
          </p>
          <p className="text-xl text-gray-600 leading-relaxed">
            Tu peux commencer quand tu veux.<br/>
            Il n'y a aucune urgence.
          </p>
        </div>
      </div>
    </section>

    {/* BLOC 6 - CONTACT / SÉCURITÉ */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xl text-gray-700 mb-6">
            Une question ? Un souci d'accès ?<br/>
            N'hésite pas à m'écrire :
          </p>
          <a 
            href="mailto:juliamamancoach@gmail.com"
            className="inline-flex items-center text-2xl text-terracotta hover:underline font-semibold"
          >
            <Mail className="w-6 h-6 mr-3" />
            juliamamancoach@gmail.com
          </a>
          <p className="text-lg text-gray-600 mt-4">
            Je te répondrai avec plaisir.
          </p>
        </div>
      </div>
    </section>

    {/* BLOC 7 - MOT DE FIN */}
    <section className="py-20 bg-gradient-to-br from-terracotta/10 via-orange-50 to-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-2xl text-gray-700 leading-relaxed mb-8">
            Encore merci pour ta confiance 🌿<br/>
            Je suis heureuse de t'accompagner dans ce parcours.
          </p>
          <p className="text-xl text-gray-600 mb-6">
            À très vite dans FIT POUSSETTE,
          </p>
          <p className="text-3xl font-bold text-terracotta">
            Julia 💛
          </p>
        </div>
      </div>
    </section>
  </div>
);


// Page Cartes Cadeaux
const CartesCadeauxPage = () => {
  // Liens directs Stripe pour chaque carte cadeau
  const stripeLinks = {
    '5-courses': 'https://buy.stripe.com/6oU9AM6BL4bP3bo2fh7AI00',
    '10-courses': 'https://buy.stripe.com/28E7sE4tD37Lh2e9HJ7AI01',
    '20-courses': 'https://buy.stripe.com/5kQcMY7FPfUx8vI4np7AI02'
  };

  const handlePurchase = (courseType) => {
    window.location.href = stripeLinks[courseType];
  };

  return (
    <div className="min-h-screen pt-16" data-testid="cartes-cadeaux-page">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 via-white to-terracotta/10">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-terracotta mb-8">
              Cartes Cadeaux 🎁
            </h1>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl text-gray-700 mb-6 font-light">
                Offrez un moment de bien-être 🎁
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Après paiement, vous recevrez automatiquement une jolie carte cadeau PDF prête à offrir, 
                correspondant à l'offre choisie : 5, 10 ou 20 séances de Pilates Reformer.
              </p>
              <div className="bg-terracotta/10 rounded-2xl p-6 inline-block mb-6">
                <p className="text-terracotta font-semibold">
                  🌸 Les cartes cadeaux sont valables pour réserver n'importe quelle séance de Pilates Reformer au studio Familyna à Pignan
                </p>
              </div>
              <p className="text-gray-600">
                <Mail className="inline w-4 h-4 mr-2" />
                Questions ? Contactez-nous : <a href="mailto:juliamamancoach@gmail.com" className="text-terracotta hover:underline font-semibold">juliamamancoach@gmail.com</a>
              </p>
            </div>
          </div>

        {/* Cartes Cadeaux */}
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Carte 5 cours */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-terracotta/20">
              <div className="text-center mb-8">
                <div className="bg-gradient-to-br from-terracotta/20 to-orange-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Gift className="w-10 h-10 text-terracotta" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Carte Cadeau
                </h3>
                <div className="bg-terracotta/10 rounded-xl p-4 mb-6">
                  <div className="text-4xl font-bold text-terracotta mb-2">5 Cours</div>
                  <p className="text-sm text-gray-600">Pilates Reformer</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">5 séances de Pilates Reformer</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">Valable 6 mois</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">PDF prêt à offrir</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">Code cadeau unique</span>
                </div>
              </div>

              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-terracotta mb-2">120€</div>
                <p className="text-sm text-gray-500">soit 24€ par cours</p>
              </div>

              <Button 
                onClick={() => handlePurchase('5-courses')}
                className="w-full bg-terracotta hover:bg-terracotta/90 text-white py-4 rounded-xl text-lg font-semibold"
              >
                <Gift className="w-5 h-5 mr-2" />
                Acheter - 120€
              </Button>
            </div>

            {/* Carte 10 cours - POPULAIRE */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-terracotta relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-terracotta text-white px-6 py-2 rounded-full text-sm font-bold">
                  ⭐ POPULAIRE
                </div>
              </div>
              
              <div className="text-center mb-8 pt-4">
                <div className="bg-gradient-to-br from-terracotta to-orange-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Gift className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Carte Cadeau
                </h3>
                <div className="bg-terracotta/10 rounded-xl p-4 mb-6">
                  <div className="text-4xl font-bold text-terracotta mb-2">10 Cours</div>
                  <p className="text-sm text-gray-600">Pilates Reformer</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">10 séances de Pilates Reformer</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">Valable 6 mois</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">PDF prêt à offrir</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                  <span className="text-gray-700">Code cadeau unique</span>
                </div>
              </div>

              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-terracotta mb-2">230€</div>
                <p className="text-sm text-gray-500">soit 23€ par cours</p>
              </div>

              <Button 
                onClick={() => handlePurchase('10-courses')}
                className="w-full bg-terracotta hover:bg-terracotta/90 text-white py-4 rounded-xl text-lg font-semibold"
              >
                <Gift className="w-5 h-5 mr-2" />
                Acheter - 230€
              </Button>
            </div>

            {/* Carte 20 cours */}
            <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-terracotta/20">
              <div className="text-center mb-8">
                <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                  <Gift className="w-10 h-10 text-orange-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Carte Cadeau
                </h3>
                <div className="bg-orange-50 rounded-xl p-4 mb-6">
                  <div className="text-4xl font-bold text-orange-600 mb-2">20 Cours</div>
                  <p className="text-sm text-gray-600">Pilates Reformer</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700">20 séances de Pilates Reformer</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700">Valable 8 mois</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700">PDF prêt à offrir</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span className="text-gray-700">Code cadeau unique</span>
                </div>
              </div>

              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-orange-600 mb-2">400€</div>
                <p className="text-sm text-gray-500">soit 20€ par cours</p>
              </div>

              <Button 
                onClick={() => handlePurchase('20-courses')}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white py-4 rounded-xl text-lg font-semibold"
              >
                <Gift className="w-5 h-5 mr-2" />
                Acheter - 400€
              </Button>
            </div>
          </div>
        </div>

        {/* Section informative */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-terracotta mb-6 text-center">
              📧 Comment ça marche ?
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Vous payez</h4>
                <p className="text-sm text-gray-600">Choisissez votre formule et procédez au paiement sécurisé</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-orange-600">2</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Vous recevez</h4>
                <p className="text-sm text-gray-600">PDF de la carte cadeau + code unique par email instantanément</p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Vous offrez</h4>
                <p className="text-sm text-gray-600">La bénéficiaire utilise le code pour réserver ses cours</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
  );
};

// Page de succès après achat
const GiftCardSuccessPage = () => (
  <div className="min-h-screen pt-16 bg-gradient-to-br from-green-50 via-white to-terracotta/10">
    <div className="container mx-auto px-4 py-20">
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-white rounded-3xl p-12 shadow-2xl">
          <div className="bg-green-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Paiement réussi ! 🎉
          </h1>
          <p className="text-xl text-gray-700 mb-8 leading-relaxed">
            Merci pour votre achat ! Vous allez recevoir votre carte cadeau par email dans quelques instants.
          </p>
          
          <div className="bg-terracotta/10 rounded-2xl p-6 mb-8">
            <p className="text-gray-700 mb-4">
              📧 Vérifiez votre boîte mail (et vos spams) pour télécharger votre carte cadeau en PDF.
            </p>
            <p className="text-gray-600 text-sm">
              La carte cadeau contient un code unique à utiliser pour réserver les cours au studio.
            </p>
          </div>
          
          <Link 
            to="/" 
            className="inline-block bg-terracotta text-white px-8 py-4 rounded-full font-semibold hover:bg-terracotta/90 transition-colors"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    </div>
  </div>
);


// Main App Component
function App() {
  return (
    <BrowserRouter>
      <div className="App">
        <Navigation />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/pilates-reformer" element={<PilatesReformerPage />} />
          <Route path="/maman-bebe" element={<MamanBebePage />} />
          <Route path="/kangoo-jumps" element={<KangooJumpsPage />} />
          <Route path="/tarifs" element={<TarifsPage />} />
          <Route path="/programmes-en-ligne" element={<FitPoussettePage />} />
          <Route path="/bienvenue-fit-poussette" element={<BienvenueFitPoussettePage />} />
          <Route path="/cartes-cadeaux" element={<CartesCadeauxPage />} />
          <Route path="/cartes-cadeaux/success" element={<GiftCardSuccessPage />} />
          <Route path="/contact" element={<ContactPage />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;